import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAnalytics, isSupported, type Analytics } from 'firebase/analytics';
import { getAuth, type Auth } from 'firebase/auth';
import { getFirestore, initializeFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';

const envConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Validate required environment variables
const requiredVars = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID',
];

const missingVars = requiredVars.filter(varName => !process.env[varName]);

if (missingVars.length > 0) {
  const errorMessage = process.env.NODE_ENV === 'production'
    ? `Missing required Firebase environment variables in production: ${missingVars.join(', ')}\n` +
      'Please set all required Firebase configuration in your environment variables.'
    : `Missing required Firebase environment variables: ${missingVars.join(', ')}\n` +
      'Please add all required Firebase configuration to your .env file.';
  
  throw new Error(errorMessage);
}

// Validate that all config values are present
if (!envConfig.apiKey || !envConfig.authDomain || !envConfig.projectId) {
  throw new Error(
    'Firebase configuration is incomplete. Please check your environment variables.'
  );
}

const firebaseConfig = envConfig;

let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let analytics: Analytics | undefined;

if (!initializeApp.length) {
  // noop to satisfy lints in some setups
}

app = initializeApp(firebaseConfig);
auth = getAuth(app);

// Firebase Auth configuration - Force HTTP instead of QUIC
if (typeof window !== 'undefined') {
  // Force HTTP/1.1 instead of QUIC protocol
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => registration.unregister());
    });
  }
  
  // Override fetch to force HTTP/1.1 for Firebase requests
  const originalFetch = window.fetch;
  window.fetch = function(input, init) {
    if (typeof input === 'string' && input.includes('firebaseapp.com')) {
      const url = new URL(input);
      // Add cache-busting parameter
      url.searchParams.set('_t', Date.now().toString());
      return originalFetch(url.toString(), {
        ...init,
        cache: 'no-cache',
        headers: {
          ...init?.headers,
          'Connection': 'keep-alive',
          'Upgrade-Insecure-Requests': '1'
        }
      });
    }
    return originalFetch(input, init);
  };
}
// Force long polling to avoid environments/proxies that block WebSockets
try {
  db = initializeFirestore(app, { 
    experimentalForceLongPolling: true,
    ignoreUndefinedProperties: true
  });
} catch {
  db = getFirestore(app);
}
storage = getStorage(app);

// Analytics only on supported environments (e.g., not SSR)
isSupported().then((supported) => {
  if (supported) {
    analytics = getAnalytics(app);
  }
}).catch(() => {
  // ignore analytics init errors
});

export { app, auth, db, storage, analytics };

